import { BOOTSTRAP_QUERY_STALE_TIME, queryClient } from '@/utils/reactQueryClient';
import { createQuickLinkController } from '@saas/api-client';
import { globalAuthRequestFunction } from '../http/request';
import { makeMutation, makeQuery } from '../http/queryUtils';

const quickLinkController = createQuickLinkController(globalAuthRequestFunction);

// --- Keys factory ---

const quickLinkServiceKeys = {
	base: 'QUICK_LINK',
	quickLinks: (params: Parameters<typeof quickLinkController.findAll>[0]) =>
		[quickLinkServiceKeys.base, 'quick-links', params] as const,
};

// --- Invalidation map ---

const invalidateQuickLinkQueries = {
	all: () => queryClient.invalidateQueries({ queryKey: [quickLinkServiceKeys.base] }),
	quickLinks: () => queryClient.invalidateQueries({ queryKey: [quickLinkServiceKeys.base, 'quick-links'] }),
};

// --- Queries ---

// Every dashboard variant renders quick links, so the dashboard route loader prefetches them the
// moment the route matches (see `prefetchDashboardBootQueries`). The pinned staleness is what makes
// that pay off: with the global `staleTime: 0` the widget mounting moments later would treat the
// just-fetched entry as stale and refetch it. The mutations below invalidate explicitly, which
// ignores staleness.
const useGetQuickLinks = makeQuery({
	queryFunction: quickLinkController.findAll,
	queryKeyFactory: (params) => [...quickLinkServiceKeys.quickLinks(params)],
	defaultOptions: { staleTime: BOOTSTRAP_QUERY_STALE_TIME },
});

// --- Mutations ---

const useCreateQuickLink = makeMutation({
	mutationFunction: quickLinkController.create,
	onSuccess: () => {
		invalidateQuickLinkQueries.quickLinks();
	},
});

const useUpdateQuickLink = makeMutation({
	mutationFunction: quickLinkController.update,
	onSuccess: () => {
		invalidateQuickLinkQueries.quickLinks();
	},
});

const useDeleteQuickLink = makeMutation({
	mutationFunction: quickLinkController.delete,
	onSuccess: () => {
		invalidateQuickLinkQueries.quickLinks();
	},
});

const useReorderQuickLinks = makeMutation({
	mutationFunction: quickLinkController.reorder,
	onSuccess: () => {
		invalidateQuickLinkQueries.quickLinks();
	},
});

// --- Export ---

export const quickLinkService = {
	useGetQuickLinks,
	useCreateQuickLink,
	useUpdateQuickLink,
	useDeleteQuickLink,
	useReorderQuickLinks,
	keys: quickLinkServiceKeys,
	invalidate: invalidateQuickLinkQueries,
};
